import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { FormProvider, useForm, } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import TextInput from '@widgets/shared/components/form-controls/TextInput';
import FormField from '@enquiryForm/components/FormField';
import { FieldType } from '@enquiryForm/types/config';
import ToggleInput from '@widgets/shared/components/form-controls/ToggleInput';
import { registerFormSchema } from '@widgets/RegisterForm/validation/schema';
import convertDataAttributes from '@widgets/shared/convertDataAttributes';
import { createRef, useContext, useState } from 'react';
import { ConfigCTX } from '@voucherForm/context/config';
import DOMPurify from 'dompurify';
import 'react-loqate/dist/index.css';
import AddressInput from '@widgets/shared/components/form-controls/AddressInput';
import API from '@widgets/shared/API/connection';
import { useCallback, useEffect, useMemo } from 'preact/compat';
import FormNotices from '@widgets/shared/components/FormNotices';
import registerOrLoginAction from '@widgets/shared/registerOrLoginAction';
function Form(props) {
    const ref = createRef();
    const [isRequesting, setIsRequesting] = useState(false);
    const { email, confirmEmail, firstName, surname, mobile, dateOfBirth, addressLine1, addressLine2, addressLine3, addressTown, addressPostcode, addressCountry, marketingPreferences, password, confirmPassword, additionalContent, successUrl, buttonText, marketingPreferenceText, } = props;
    const { overviewContent } = useContext(ConfigCTX);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const handleNoticeClose = useCallback(() => {
        setErrorMessage(undefined);
    }, []);
    const form = useForm({
        mode: 'onChange',
        resolver: yupResolver(registerFormSchema),
        defaultValues: {
            firstName,
            surname,
            email,
            confirmEmail,
            mobile,
            dateOfBirth: dateOfBirth ? new Date(dateOfBirth) : new Date(Date.now()),
            addressLine1,
            addressLine2,
            addressTown,
            addressPostcode,
            addressCountry,
            marketingPreferences: marketingPreferences ?? false,
            password,
            confirmPassword,
        },
    });
    const { formState: { isValid, errors }, setError, handleSubmit, getValues, watch, trigger, } = form;
    const formInvalid = useMemo(() => !isValid || isRequesting, [isValid, isRequesting]);
    const emailAddress = watch('email');
    const confirmEmailAddress = watch('confirmEmail');
    const passwordField = watch('password');
    const confirmPasswordField = watch('confirmPassword');
    useEffect(() => {
        // doing individually rather as an array to stop confirm prematurely triggering
        if (emailAddress !== '') {
            trigger('email')
                .then(() => { });
        }
        if (confirmEmailAddress !== '') {
            trigger('confirmEmail')
                .then(() => { });
        }
    }, [emailAddress, confirmEmailAddress, trigger]);
    useEffect(() => {
        if (passwordField) {
            trigger('password')
                .then(() => { });
        }
        if (confirmPasswordField) {
            trigger('confirmPassword')
                .then(() => { });
        }
    }, [passwordField, confirmPasswordField, trigger]);
    const handleSubmitEvent = () => {
        setIsRequesting(true);
        const values = getValues();
        const formEl = ref.current;
        API.register(values).then((res) => {
            registerOrLoginAction(res, formEl, () => { }, (theErrors) => {
                theErrors.forEach((error) => {
                    if (error.field) {
                        setError(error.field, { message: error.message }, { shouldFocus: false });
                    }
                });
                if (res.message) {
                    setErrorMessage(res.message);
                }
            }, successUrl);
        })
            .finally(() => {
            setIsRequesting(false);
        });
    };
    return (_jsx(FormProvider, { ...form, children: _jsxs("form", { method: "POST", ref: ref, onSubmit: handleSubmit(handleSubmitEvent), noValidate: true, children: [errorMessage && (_jsx("div", { style: { marginBottom: '1rem' }, children: _jsx(FormNotices, { message: errorMessage, type: "error", handleClose: handleNoticeClose }) })), _jsxs("div", { className: "form__fields", children: [_jsx("div", { className: "form__content", children: _jsx("h2", { className: "h4", children: "Create an account" }) }), _jsx(TextInput, { inputAttrs: {
                                id: 'first-name',
                                name: 'firstName',
                                type: 'text',
                            }, labelAttrs: {
                                htmlFor: 'first-name',
                                text: 'First Name',
                                isFieldRequired: true,
                            } }), _jsx(TextInput, { inputAttrs: {
                                id: 'surname',
                                name: 'surname',
                                type: 'text',
                            }, labelAttrs: {
                                htmlFor: 'surname',
                                text: 'Surname',
                                isFieldRequired: true,
                            } }), _jsx(TextInput, { inputAttrs: {
                                id: 'email',
                                name: 'email',
                                type: 'email',
                            }, labelAttrs: {
                                htmlFor: 'email',
                                text: 'Email',
                                isFieldRequired: true,
                            } }), _jsx(TextInput, { inputAttrs: {
                                id: 'confirm-email',
                                name: 'confirmEmail',
                                type: 'email',
                            }, labelAttrs: {
                                htmlFor: 'confirm-email',
                                text: 'Confirm Email',
                                isFieldRequired: true,
                            } }), _jsx(FormField, { id: "mobile", name: "mobile", label: "Mobile Number", required: true, type: FieldType.PhoneInput, defaultValue: "", placeholder: "", content: "" }), _jsx(FormField, { id: "date-of-birth", name: "dateOfBirth", label: "Date of Birth", required: true, type: FieldType.DatepickerInput, content: "", maxDate: new Date() }), _jsx(AddressInput, { addressLineOneName: "addressLine1", addressLineTwoName: "addressLine2", addressTownName: "addressTown", addressPostcodeName: "addressPostcode", addressCountry: "addressCountry", inputAttrs: {
                                name: 'address-search',
                                id: 'address-search',
                            }, labelAttrs: {
                                isFieldRequired: false,
                                text: 'Search for an address',
                            } }), _jsx(FormField, { id: "password", name: "password", label: "Password", type: FieldType.Password, required: true, content: "" }), _jsx(FormField, { id: "confirm-password", name: "confirmPassword", label: "Confirm Password", type: FieldType.Password, required: true, content: "" }), _jsxs("div", { className: "form__content", children: [_jsx("h3", { children: "Marketing Preferences" }), _jsx(ToggleInput, { inputAttrs: {
                                        id: 'marketing-preferences',
                                        name: 'marketingPreferences',
                                    }, labelAttrs: {
                                        htmlFor: 'marketing-preferences',
                                        text: marketingPreferenceText ?? 'Email me with news and offers',
                                        isFieldRequired: false,
                                    } })] })] }), _jsxs("div", { className: "form__actions", children: [additionalContent && (_jsx("div", { className: "overview__extra", dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(additionalContent) } })), _jsx("button", { type: "submit", className: "btn", disabled: formInvalid, children: buttonText ?? 'Register' })] })] }) }));
}
export default function RegisterForm(props) {
    const { dataset } = props;
    const formConfig = convertDataAttributes(dataset ?? {});
    return (_jsx(Form, { ...formConfig }));
}
